const SYNC_POLL_INITIAL_DELAY_MS = 2000;
const SYNC_POLL_FAST_INTERVAL_MS = 2000;
const SYNC_POLL_MEDIUM_INTERVAL_MS = 4000;
const SYNC_POLL_SLOW_INTERVAL_MS = 8000;
const SYNC_INDICATOR_FADE_MS = 300;

/**
 * Polls the automatic-list sync state and owns the `[data-lists-sync-status]` banner; its copy comes from the
 * banner's `data-sync-pending-text`/`data-sync-failed-text`, set per page by the template. Once the sync has settled
 * without failure, `refresh` brings the page's own content up to date: `true` ends polling and fades the banner
 * out, `false` (stale, busy or failed refresh) tries again on the next tick.
 *
 * @param {{
 *   statusElement: HTMLElement,
 *   syncStatusUrl: string,
 *   refresh: () => Promise<boolean>
 * }} options
 */
export function startAutomaticListsSyncPolling({ statusElement, syncStatusUrl, refresh }) {
    if (!(statusElement instanceof HTMLElement) || syncStatusUrl === '' || typeof refresh !== 'function') {
        return;
    }

    const indicator = statusElement.querySelector('.lists-sync-indicator');
    let attempts = 0;
    let isPollingInFlight = false;
    let isPollingActive = true;
    let nextPollTimeoutId = 0;
    scheduleNextPoll(SYNC_POLL_INITIAL_DELAY_MS);

    function getPollIntervalMs() {
        if (attempts < 5) {
            return SYNC_POLL_FAST_INTERVAL_MS;
        }

        if (attempts < 10) {
            return SYNC_POLL_MEDIUM_INTERVAL_MS;
        }

        return SYNC_POLL_SLOW_INTERVAL_MS;
    }

    function scheduleNextPoll(delayMs = getPollIntervalMs()) {
        if (!isPollingActive) {
            return;
        }

        if (nextPollTimeoutId !== 0) {
            window.clearTimeout(nextPollTimeoutId);
        }

        nextPollTimeoutId = window.setTimeout(() => {
            nextPollTimeoutId = 0;
            void pollSyncStatus();
        }, delayMs);
    }

    async function pollSyncStatus() {
        if (!isPollingActive || isPollingInFlight) {
            return;
        }

        isPollingInFlight = true;
        attempts += 1;

        try {
            const syncStatus = await fetchCurrentSyncStatus();
            if (syncStatus.failed) {
                showSyncStatus(true);
                if (!syncStatus.pending) {
                    stopPolling();

                    return;
                }
            } else if (syncStatus.pending) {
                showSyncStatus(false);
            }
            if (!syncStatus.failed && !syncStatus.pending) {
                const refreshed = await refresh();
                if (refreshed) {
                    stopPolling();
                    removeSyncStatus();

                    return;
                }
            }
        } catch (_error) {
            // Continue polling silently; transient failures can recover on next attempt.
        } finally {
            isPollingInFlight = false;
        }

        scheduleNextPoll();
    }

    async function fetchCurrentSyncStatus() {
        const response = await fetch(syncStatusUrl, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Cache-Control': 'no-cache',
            },
        });
        if (!response.ok) {
            throw new Error(`Automatic sync-status fetch failed with status ${response.status}`);
        }

        const payload = await response.json();
        if (typeof payload !== 'object' || payload === null) {
            throw new Error('Automatic sync-status payload is invalid.');
        }

        const pending = payload.pending;
        if (typeof pending !== 'boolean') {
            throw new Error('Automatic sync-status payload is missing pending flag.');
        }

        const failed = payload.failed;
        if (typeof failed !== 'boolean') {
            throw new Error('Automatic sync-status payload is missing failed flag.');
        }

        return { pending, failed };
    }

    function stopPolling() {
        if (!isPollingActive) {
            return;
        }

        isPollingActive = false;

        if (nextPollTimeoutId !== 0) {
            window.clearTimeout(nextPollTimeoutId);
            nextPollTimeoutId = 0;
        }
    }

    function removeSyncStatus() {
        if (indicator instanceof HTMLElement) {
            indicator.classList.add('is-fading-out');
        }
        window.setTimeout(() => {
            statusElement.remove();
        }, SYNC_INDICATOR_FADE_MS);
    }

    function showSyncStatus(failed) {
        statusElement.classList.toggle('is-failed', failed);
        if (indicator instanceof HTMLElement) {
            indicator.setAttribute('role', failed ? 'alert' : 'status');
            indicator.setAttribute('aria-live', failed ? 'assertive' : 'polite');
            const text = indicator.querySelector('.lists-sync-indicator-text');
            if (text instanceof HTMLElement) {
                text.textContent = String(
                    (failed ? statusElement.dataset.syncFailedText : statusElement.dataset.syncPendingText) ?? '',
                );
            }
        }
    }
}
